/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { JSX } from 'react';

import { jsx } from '@compiled/react';

import LinkIcon from '@atlaskit/icon/core/link';
import {
	isGoogleDocument,
	isGooglePresentation,
	isGoogleResult,
	isGoogleSpreadsheet,
	isSharepointDocument,
	isSharepointPresentation,
	isSharepointResult,
	isSharepointSpreadsheet,
	isSlackMessage,
	type SearchResultEntity,
	type SearchResultGraphDocument,
	type SearchThirdPartyDocumentPartial,
} from '@atlassian/dynamic-cards-search-client';

import { GoogleDocResultIcon } from '../constants/icons/google-document';
import { GooglePresentationIcon } from '../constants/icons/google-presentation';
import { GoogleSpreadsheetIcon } from '../constants/icons/google-spreadsheet';
import { SharepointDocumentIcon } from '../constants/icons/sharepoint-document';
import { SharepointPresentationIcon } from '../constants/icons/sharepoint-presentation';
import { SharepointSpreadsheetIcon } from '../constants/icons/sharepoint-spreadsheet';
import { SlackIcon } from '../constants/icons/slack';
import { ProductKeys } from '../constants/products';
import { ProductIcon } from '../ui/product-icon';

export const getIcon = (
	partial: SearchThirdPartyDocumentPartial | SearchResultGraphDocument<SearchResultEntity>,
	selectedProduct?: ProductKeys,
	label = '',
): JSX.Element | undefined => {
	const getGoogleIconGlyph = () => {
		if (isGoogleDocument(partial)) {
			return GoogleDocResultIcon;
		}
		if (isGooglePresentation(partial)) {
			return GooglePresentationIcon;
		}
		if (isGoogleSpreadsheet(partial)) {
			return GoogleSpreadsheetIcon;
		}
	};

	const getSharepointIconGlyph = () => {
		if (isSharepointDocument(partial)) {
			return SharepointDocumentIcon;
		}
		if (isSharepointPresentation(partial)) {
			return SharepointPresentationIcon;
		}
		if (isSharepointSpreadsheet(partial)) {
			return SharepointSpreadsheetIcon;
		}
	};

	if (isGoogleResult(partial)) {
		return selectedProduct ? (
			getGoogleIconGlyph()?.({
				'aria-label': label,
			})
		) : (
			<ProductIcon product={ProductKeys.GoogleDrive} />
		);
	}

	if (isSlackMessage(partial)) {
		return selectedProduct ? (
			<SlackIcon aria-label={label} />
		) : (
			<ProductIcon product={ProductKeys.Slack} />
		);
	}

	if (isSharepointResult(partial)) {
		return selectedProduct ? (
			getSharepointIconGlyph()?.({
				'aria-label': label,
			})
		) : (
			<ProductIcon product={ProductKeys.Sharepoint} />
		);
	}

	return <LinkIcon color="currentColor" spacing="spacious" label="" />;
};

export const getLabel = (partial: SearchThirdPartyDocumentPartial): "" | "Slack" | "Docs" | "Slides" | "Sheets" | "Word" | "PowerPoint" | "Excel" => {
	if (isSlackMessage(partial)) {
		return 'Slack';
	}
	if (isGoogleDocument(partial)) {
		return 'Docs';
	}
	if (isGooglePresentation(partial)) {
		return 'Slides';
	}
	if (isGoogleSpreadsheet(partial)) {
		return 'Sheets';
	}
	if (isSharepointDocument(partial)) {
		return 'Word';
	}
	if (isSharepointPresentation(partial)) {
		return 'PowerPoint';
	}
	if (isSharepointSpreadsheet(partial)) {
		return 'Excel';
	}

	return '';
};
